import { create } from 'zustand';
import type { User, AppType, TelegramWebApp } from '@/types';
import { platformService } from '../services/platform';

export type ActiveTab =
    | 'home'
    | 'vocab'
    | 'profile'
    | 'info'
    | 'exams'
    | 'vocabulary'
    | 'guide'
    | 'practice'
    | 'tutor'
    | 'grammar'
    // GreekBrain v2 shell (products/greekbrain/v2) — 'vocabulary' and 'practice' are shared.
    | 'today'
    | 'exam'
    | 'interview';

interface AppState {
    user: User | null;
    appType: AppType;
    activeTab: ActiveTab;
    telegram: TelegramWebApp | null;
    isLoading: boolean;
    error: string | null;
    hasInitiallyLoaded: boolean;
    // GreekBrain subscription paywall — opened globally when a paid AI feature is blocked.
    greekbrainPaywall: { open: boolean; source: string; message?: string };
    openGreekbrainPaywall: (source: string, message?: string) => void;
    closeGreekbrainPaywall: () => void;
    setUser: (user: User | null) => void;
    setAppType: (appType: AppType) => void;
    setActiveTab: (tab: ActiveTab) => void;
    setTelegram: (telegram: TelegramWebApp) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    initializeAppType: () => Promise<void>;
    setHasInitiallyLoaded: (value: boolean) => void;
    // True while a bottom sheet / modal overlay is open, so the global bottom
    // navigation bar can hide itself instead of overlapping the sheet.
    isBottomSheetOpen: boolean;
    setBottomSheetOpen: (open: boolean) => void;
    // The setup prompt has moved off the home feed onto Profile, so something
    // has to say "there is something for you in there" — a dot on the tab.
    // Set from whichever payload arrives first (dashboard or profile); the
    // navigation bar itself fetches nothing.
    setupAttention: boolean;
    setSetupAttention: (value: boolean) => void;
    // GreekBrain v2 shell is on (products/greekbrain decides at load, from the
    // host / ?ui=). Core reads this instead of importing the product's variant.ts.
    greekbrainV2: boolean;
    setGreekbrainV2: (value: boolean) => void;
    reset: () => void;
}

const VALID_TABS: ActiveTab[] = [
    'home', 'vocab', 'profile', 'info', 'exams',
    'vocabulary', 'guide', 'practice', 'tutor', 'grammar',
    'today', 'exam', 'interview',
];

const getSavedActiveTab = (): ActiveTab => {
    const savedTab = localStorage.getItem('activeTab');
    if (savedTab && (VALID_TABS as string[]).includes(savedTab)) {
        localStorage.removeItem('activeTab');
        return savedTab as ActiveTab;
    }
    return 'home';
};

const initialState = {
    user: null,
    appType: 'hallouminati' as AppType,
    activeTab: getSavedActiveTab(),
    telegram: null,
    isLoading: false,
    error: null,
    hasInitiallyLoaded: false,
    greekbrainPaywall: { open: false, source: '' },
    isBottomSheetOpen: false,
    setupAttention: false,
};

export const useAppStore = create<AppState>((set) => ({
    ...initialState,

    openGreekbrainPaywall: (source, message) =>
        set({ greekbrainPaywall: { open: true, source, message } }),
    closeGreekbrainPaywall: () =>
        set((state) => ({ greekbrainPaywall: { ...state.greekbrainPaywall, open: false } })),

    setUser: (user) => {
        set({ user });
    },
    setAppType: (appType) => set({ appType }),
    setActiveTab: (activeTab) => set({ activeTab }),
    setTelegram: (telegram) => set({ telegram }),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),

    initializeAppType: async () => {
        const detectedAppType = await platformService.getAppType();
        set({ appType: detectedAppType });
    },
    setHasInitiallyLoaded: (value: boolean) => set({ hasInitiallyLoaded: value }),
    setBottomSheetOpen: (open) => set({ isBottomSheetOpen: open }),
    setSetupAttention: (setupAttention) => set({ setupAttention }),

    // Not in initialState on purpose: a load-time fact, must survive reset().
    greekbrainV2: false,
    setGreekbrainV2: (greekbrainV2) => set({ greekbrainV2 }),

    reset: () => set({ ...initialState, activeTab: 'home' }),
}));